import { useState, useMemo, useCallback } from 'react';
import { fetchOptimize } from '../../utils/connectivityApi';
import type { OptimizeResult } from '../../types';

interface Device {
  id: string;
  name: string;
}

export function ConnectivityOptimizeTab({ dcCode, devices }: { dcCode: string, devices: Device[] }) {
  const [optSrc, setOptSrc] = useState('');
  const [optTgt, setOptTgt] = useState('');
  const [optResult, setOptResult] = useState<OptimizeResult | null>(null);
  const [optLoading, setOptLoading] = useState(false);

  const options = useMemo(() => devices.map((d) => ({ value: d.id, label: d.name })), [devices]);

  const handleOptimize = useCallback(async () => {
    if (!optSrc || !optTgt) return;
    setOptLoading(true);
    try {
      const res = await fetchOptimize(dcCode, optSrc, optTgt);
      setOptResult(res);
    } catch {
      setOptResult(null);
    } finally {
      setOptLoading(false);
    }
  }, [dcCode, optSrc, optTgt]);

  return (
    <div>
      <h2 className="text-base font-semibold font-mono text-slate-100 mb-4">Оптимизация маршрутизации</h2>
      <div className="flex flex-col sm:flex-row gap-4 items-end mb-6 font-mono text-xs">
        <div className="flex-1 w-full">
          <label className="block text-slate-400 mb-1.5">Источник</label>
          <select value={optSrc} onChange={(e) => setOptSrc(e.target.value)} className="w-full bg-slate-950 border border-slate-800/80 rounded-lg px-3 py-2 text-slate-200 focus:border-cyan-500 outline-none">
            <option value="">Выберите устройство</option>
            {options.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="flex-1 w-full">
          <label className="block text-slate-400 mb-1.5">Назначение</label>
          <select value={optTgt} onChange={(e) => setOptTgt(e.target.value)} className="w-full bg-slate-950 border border-slate-800/80 rounded-lg px-3 py-2 text-slate-200 focus:border-cyan-500 outline-none">
            <option value="">Выберите устройство</option>
            {options.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <button
          onClick={handleOptimize}
          disabled={!optSrc || !optTgt || optLoading}
          className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg disabled:opacity-50 transition-colors whitespace-nowrap"
        >
          {optLoading ? 'Анализ...' : 'Анализировать'}
        </button>
      </div>
      {optResult && (
        <div className="font-mono text-xs">
          {!optResult.feasible ? (
            <div className="text-amber-400 bg-amber-950/20 border border-amber-800/40 rounded-lg p-3">
              {optResult.reason ?? 'Невозможно определить маршрут'}
            </div>
          ) : (
            <div>
              <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-3">Рекомендации ({optResult.suggestions.length})</h3>
              <div className="space-y-2">
                {optResult.suggestions.slice(0, 10).map((s, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-slate-950/40 border border-slate-800/50 rounded-lg px-3 py-2">
                    <div>
                      <span className="font-medium text-slate-200">{s.source_port}</span> {' → '} <span className="font-medium text-slate-200">{s.target_port}</span>
                    </div>
                    <div className="flex items-center gap-3 text-slate-400">
                      <span>[{s.recommended_cable_type}]</span>
                      <span className="text-emerald-400 font-semibold">{Math.round(s.confidence * 100)}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
